import { useMemo } from 'react'
import { ArrowRight, Check, Eye, Save } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BackButton, ContentLoader, PageHeader } from '@/components/atoms'
import { cn } from '@/lib/utils'
import { useBusiness } from '@/contexts/BusinessContext'

import { BUILDER_STEPS, useCampaignBuilder, type BuilderStep } from '@/hooks/features/campaigns'
import {
  CampaignPreviewPane,
  LaunchStep,
  PriceExample,
  Step1Details,
  TemplatePicker,
  ValidationBanner,
} from '@/components/features/campaigns/CampaignBuilder'
import { CampaignOfferRulesEditor } from '@/components/features/campaigns/CampaignOfferRulesEditor'
import { CampaignUiContentEditor } from '@/components/features/campaigns/CampaignUiContentEditor'
import { CampaignPageBuilder } from '@/components/features/campaigns/CampaignPageBuilder'
import { CampaignStatusBadge } from '@/components/features/campaigns/CampaignsTable/CampaignStatusBadge'

const STEP_LABELS: Record<BuilderStep, { title: string; hint: string }> = {
  offer: { title: 'Offer', hint: 'What the customer gets' },
  page: { title: 'Page', hint: 'Design the landing page' },
  launch: { title: 'Launch', hint: 'Check, promote and publish' },
}

/** Campaign builder 2.0: Offer → Page → Launch, every step revisitable, the draft autosaved (FR-CB4). */
export const CampaignBuilder = () => {
  const navigate = useNavigate()
  const ctx = useCampaignBuilder()
  const { profile } = useBusiness()
  const currency = profile?.app_settings?.currency

  const draft = useMemo(
    () => ({
      name: ctx.step1.name,
      offer_rules: ctx.step2.offer_rules as Record<string, unknown>,
      ui_content: ctx.step3.ui_content as Record<string, unknown>,
    }),
    [ctx.step1.name, ctx.step2, ctx.step3],
  )

  if (ctx.loadingOffer) return <ContentLoader />

  const stepIndex = BUILDER_STEPS.indexOf(ctx.step)
  const next = BUILDER_STEPS[stepIndex + 1]

  return (
    <div className="space-y-4 pb-20">
      {/* Sticky header within the scrollable <main>: back, title, status, actions and steps */}
      <div className="sticky top-[52px] z-20 -mx-1 space-y-3 border-b border-border-subtle bg-page px-1 pb-3 pt-2 lg:top-0">
        <PageHeader
          size="large"
          className="mb-0"
          title={ctx.step1.name || (ctx.isEditMode ? 'Edit campaign' : 'New campaign')}
          badge={<CampaignStatusBadge campaign={ctx.step1} className="shrink-0" />}
          description={
            ctx.dirty ? 'Unsaved changes — kept in this browser until you save' : ctx.savedOffer ? 'All changes saved' : 'Not saved yet'
          }
          back={<BackButton onClick={() => navigate('/campaigns')} label="Back to campaigns" />}
          actions={
            <>
              {ctx.savedOffer && (
                <Button variant="outline" onClick={ctx.previewCampaign} disabled={ctx.submitLoading || ctx.previewLoading}>
                  <Eye />
                  {ctx.previewLoading ? 'Opening…' : 'Open in new tab'}
                </Button>
              )}
              <Button variant="secondary" onClick={ctx.saveDraft} disabled={ctx.submitLoading}>
                <Save />
                {ctx.submitLoading ? 'Saving…' : 'Save'}
              </Button>
            </>
          }
        />

        <nav aria-label="Campaign steps">
          <ol className="no-scrollbar flex gap-1 overflow-x-auto">
            {BUILDER_STEPS.map((s, i) => {
              const current = s === ctx.step
              const done = i < stepIndex
              return (
                <li key={s} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => ctx.goTo(s)}
                    disabled={ctx.submitLoading}
                    aria-current={current ? 'step' : undefined}
                    title={STEP_LABELS[s].hint}
                    className={cn(
                      'flex h-7 items-center gap-1.5 rounded-md px-3 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
                      current ? 'bg-selected text-ink' : 'text-text-2 hover:bg-subdued hover:text-ink',
                    )}
                  >
                    {done ? (
                      <Check className="size-4 text-success-dark" aria-hidden />
                    ) : (
                      <span className="text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                    )}
                    {STEP_LABELS[s].title}
                    <span className="sr-only">{done ? ' (done)' : ''}</span>
                  </button>
                </li>
              )
            })}
          </ol>
        </nav>
      </div>

      {ctx.restorable && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-warning-lighter px-4 py-3 text-sm text-warning-dark" role="status">
          <span>You have unsaved changes from {new Date(ctx.restorable.savedAt).toLocaleString()}.</span>
          <span className="flex gap-2">
            <Button variant="secondary" onClick={ctx.restoreDraft}>Restore</Button>
            <Button variant="ghost" onClick={ctx.discardDraft}>Discard</Button>
          </span>
        </div>
      )}

      {ctx.formErrors.length > 0 && <ValidationBanner errors={ctx.formErrors} />}

      {ctx.step === 'offer' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-6">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Campaign details</CardTitle>
              </CardHeader>
              <CardContent>
                <Step1Details values={ctx.step1} onChange={ctx.setStep1} onTypeChange={ctx.handleTypeChange} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Offer rules</CardTitle>
                <CardDescription>What the customer must buy and what they get.</CardDescription>
              </CardHeader>
              <CardContent>
                {ctx.step1.type ? (
                  <CampaignOfferRulesEditor
                    type={ctx.step1.type}
                    rules={ctx.step2.offer_rules}
                    onChange={(rules) => ctx.setStep2({ offer_rules: rules })}
                  />
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">Choose an offer type above.</p>
                )}
              </CardContent>
            </Card>
          </div>
          {ctx.step1.type && (
            <div className="lg:sticky lg:top-44 lg:self-start">
              <PriceExample type={ctx.step1.type} rules={ctx.step2.offer_rules} catalog={ctx.catalog} currency={currency} />
            </div>
          )}
        </div>
      )}

      {ctx.step === 'page' && ctx.step1.type && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] xl:gap-6">
          <div className="space-y-4">
            <TemplatePicker
              offerType={ctx.step1.type}
              blocks={ctx.step3.ui_content.blocks ?? []}
              onApply={(blocks) => ctx.setStep3({ ui_content: { ...ctx.step3.ui_content, blocks } })}
            />
            <Card>
              <CardHeader>
                <CardTitle>Page blocks</CardTitle>
                <CardDescription>Drag to reorder; click a block to edit it. The page follows your store theme.</CardDescription>
              </CardHeader>
              <CardContent>
                <CampaignPageBuilder
                  offerType={ctx.step1.type}
                  blocks={ctx.step3.ui_content.blocks ?? []}
                  onChange={(blocks) => ctx.setStep3({ ui_content: { ...ctx.step3.ui_content, blocks } })}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Page settings</CardTitle>
                <CardDescription>Search and social preview, and the campaign accent colour.</CardDescription>
              </CardHeader>
              <CardContent>
                <CampaignUiContentEditor
                  values={ctx.step3.ui_content}
                  onChange={(ui_content) => ctx.setStep3({ ui_content })}
                />
              </CardContent>
            </Card>
          </div>
          <div className="h-[calc(100vh-12rem)] min-h-[560px] xl:sticky xl:top-44">
            {ctx.origin && ctx.savedOffer?.slug ? (
              <CampaignPreviewPane
                origin={ctx.origin}
                offer={{ id: ctx.savedOffer.id, slug: ctx.savedOffer.slug }}
                draft={draft}
                onMobileViewed={() => ctx.setMobileReviewed(true)}
              />
            ) : (
              <div className="grid h-full place-items-center rounded-lg border border-border bg-subdued p-6 text-center text-sm text-muted-foreground">
                {ctx.origin ? 'Save the campaign to see the live preview.' : 'Live preview needs the store domain. Set it in your business profile.'}
              </div>
            )}
          </div>
        </div>
      )}

      {ctx.step === 'launch' && <LaunchStep ctx={ctx} />}

      {next && (
        <div className="flex justify-end">
          <Button onClick={() => ctx.goTo(next)} disabled={ctx.submitLoading}>
            Next: {STEP_LABELS[next].title}
            <ArrowRight />
          </Button>
        </div>
      )}
    </div>
  )
}
